import React, { useEffect, useState } from "react";
import { ColumnsType } from "antd/lib/table";
import styled from "styled-components";
import { Button, Col, message, Modal, Row, Skeleton, Space, Tabs } from "antd";
import {
  onlineOrderState,
  fetchOrderDetail,
  fetchProductList,
  fetchSubmitOrder,
  setProductFilter,
  fetchDeleteProduct,
  setHideUserInfo,
} from "@redux/onlineOrderSlice";
import IntegratedTable from "@component/Table/IntegratedTable";
import { storageTypeOptions } from "@constant/StorageType";
import onlineOrderApi, { Product, ProductListParams } from "@api/throughShipment/onlineOrderApi";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { PlusCircleTwoTone, ExclamationCircleOutlined } from "@ant-design/icons";
import { hideLoading, showLoading } from "@redux/notifySlice";
import ImportFileModal from "@component/ImportFileModal";
import PageTitle from "@component/PageTitle";
import DashBoard from "@component/Common/DashBoard";
import { translateErrors } from "@utils/translateError";
import EyeOpenIcon from "@assets/Eye-On@3x.png";
import EyeOffIcon from "@assets/Eye-Off@3x.png";
import OrderTab from "./OrderTab";
import EditProductPopup from "./EditProductPopup";
import EditPopup from "../EditPopup";

const { TabPane } = Tabs;

const StyledWrapper = styled.section`
  margin: 16px 25px;
  padding: 8px 11px;
`;
const ModalContent = styled.section`
  height: 150px;
  overflow: auto;
`;
const EyeIconImg = styled.img`
  cursor: pointer;
  position: absolute;
  top: 15px;
  left: 220px;
  width: 24px;
  height: 24px;
  margin-left: 10px;
  z-index: 2;
`;
const NoticeText = styled.p`
  margin-bottom: 15px;
`;
const Flex = styled.div`
  display: flex;
  align-items: center;
`;
const EditText = styled(Button)`
  padding: 0;
`;
const DeleteText = styled(Button)`
  margin-left: 23px;
  padding: 0;
`;

export default function Shipment() {
  const dispatch = useDispatch();
  const { orderId } = useParams();
  const { onlineOrderDetail, orderProductList, productListFilter, isProductFetching, hideUserInfo } = useSelector(
    onlineOrderState,
  );
  const [showPopup, setShowPopup] = useState<boolean>(false);

  const [activeTab, setActiveTab] = useState("order");
  const [showProductFormModal, setShowProductFormModal] = useState<boolean>(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState<Product>();
  const cannotEdit = !!onlineOrderDetail.submittedAt || !!onlineOrderDetail.destroyedAt;
  const warehouseIsTcat = onlineOrderDetail.warehouseCode === "TCAT";
  const isB2B = onlineOrderDetail.platform?.startsWith("B2B");

  const baseColumns: ColumnsType<any> = [
    { title: "ID", dataIndex: "id" },
    { title: "SKU(品號)", dataIndex: "sku" },
    { title: "商品名稱", dataIndex: "productName" },
    { title: "出貨商品數量(pcs)", dataIndex: "estimatedQty" },
    { title: "單品總金額", dataIndex: "unitPrice" },
    {
      title: "倉別",
      dataIndex: "storageType",
      render: (value: any) => storageTypeOptions.find((type) => type.key === value)?.name || "-",
    },
    { title: "商品效期", dataIndex: "effectiveDate" },

    {
      title: "批號",
      dataIndex: "batch",
    },
    {
      title: "實際出貨量（pcs)",
      dataIndex: "actualQty",
    },
    {
      title: `${cannotEdit ? "實際數量" : "庫存量"}`,
      dataIndex: "stock",
      render: (_, values) => (cannotEdit ? <span>{values.actualQty}</span> : <span>{values.stock}</span>),
    },
    { title: "barcode", dataIndex: "barcode" },
    {
      title: "",
      dataIndex: "id",
      width: 30,
      render: (productId: number, record: Product) => {
        return (
          <Flex>
            <EditText
              type="link"
              onClick={() => {
                setCurrentProduct(record);
                setShowProductFormModal(true);
              }}
              disabled={cannotEdit}
            >
              編輯
            </EditText>
            <DeleteText
              disabled={cannotEdit}
              type="link"
              onClick={() => {
                Modal.confirm({
                  title: "確定要刪除此商品嗎?",
                  icon: <ExclamationCircleOutlined />,
                  okText: "是",
                  cancelText: "否",
                  onOk() {
                    dispatch(fetchDeleteProduct({ orderId, productId }));
                  },
                });
              }}
            >
              刪除
            </DeleteText>
          </Flex>
        );
      },
    },
  ];

  const hideForTcat = ["storageType", "batch", "barcode"];

  const columns = warehouseIsTcat
    ? baseColumns.filter((col: any) => (col.dataIndex ? !hideForTcat.includes(col.dataIndex) : true))
    : baseColumns;

  function switchTab(activeKey: string) {
    setActiveTab(activeKey);
    if (activeKey !== "product") {
      dispatch(setHideUserInfo(true));
    }
  }

  useEffect(() => {
    dispatch(fetchOrderDetail(orderId));
  }, [dispatch, orderId]);

  useEffect(() => {
    dispatch(fetchProductList({ orderId, filter: productListFilter }));
  }, [dispatch, orderId, productListFilter]);

  const handleSubmit = () => {
    dispatch(fetchSubmitOrder(orderId));
  };
  const handelAddXLS = async (file: File) => {
    try {
      dispatch(showLoading());
      const response = await onlineOrderApi.fetchUploadProducts({ file, orderId });
      dispatch(fetchProductList({ orderId, filter: productListFilter }));
      dispatch(setHideUserInfo(true));
      message.success("匯入成功");
      return response;
    } catch (error: any) {
      const { result } = error;
      const translatedErrors = translateErrors(result);
      if (!(translatedErrors.length > 0)) return null;
      Modal.warning({
        title: "部分商品資料有誤，請修正",
        content: (
          <ModalContent>
            {translatedErrors.map((errorItem: any) => {
              return <NoticeText>{errorItem}</NoticeText>;
            })}
          </ModalContent>
        ),
        okText: "我知道了",
      });

      return null;
    } finally {
      dispatch(hideLoading());
    }
  };

  return (
    <StyledWrapper style={{ position: "relative", border: "1px solid #F0F0F0" }}>
      <PageTitle title={`線上出貨 - ${onlineOrderDetail.orderNumber}`} />
      {onlineOrderDetail.id !== 0 ? (
        <>
          {activeTab === "order" && (
            <EyeIconImg
              src={hideUserInfo ? EyeOffIcon : EyeOpenIcon}
              onClick={() => {
                if (hideUserInfo) {
                  Modal.confirm({
                    title: "此頁面資訊含有個人資料，為保護個資安全，確定要顯示嗎?",
                    icon: <ExclamationCircleOutlined />,
                    okText: "確定",
                    cancelText: "取消",
                    onOk() {
                      dispatch(setHideUserInfo(!hideUserInfo));
                    },
                  });
                  return;
                }
                dispatch(setHideUserInfo(!hideUserInfo));
              }}
            />
          )}
          <Tabs onChange={switchTab} type="card">
            <TabPane tab="編輯出貨單" key="order">
              <OrderTab onlineOrderDetail={onlineOrderDetail} hideUserInfo={hideUserInfo} />
            </TabPane>
            <TabPane tab="出貨商品" key="product">
              <Row gutter={[8, 8]}>
                <Col>
                  <Button
                    type="primary"
                    icon={<PlusCircleTwoTone twoToneColor="#1890FF" />}
                    onClick={() => {
                      setCurrentProduct(undefined);
                      setShowProductFormModal(true);
                    }}
                    disabled={cannotEdit}
                  >
                    新增商品
                  </Button>
                </Col>
                {!warehouseIsTcat && (
                  <Col>
                    <Button onClick={() => setShowImportModal(true)} disabled={cannotEdit}>
                      匯入商品
                    </Button>
                  </Col>
                )}

                <Col>
                  <DashBoard
                    dataset={[
                      {
                        name: "出貨總數",
                        value: orderProductList.results.reduce((acc, cur) => acc + cur.estimatedQty, 0),
                      },
                    ]}
                  />
                </Col>
              </Row>
              {/* 還貨商品列表 */}
              <IntegratedTable
                isPagination
                paginationConfig={{
                  totalCount: orderProductList.count,
                  updateFilterParams: (filter: ProductListParams) => dispatch(setProductFilter(filter)),
                  filterParams: productListFilter,
                }}
                dataSource={orderProductList.results}
                loading={isProductFetching}
                columns={columns}
                scroll={{ x: "max-content" }}
                onRow={(record: any) => ({
                  className: record.destroyedAt ? "ur-disabled-text" : "",
                })}
              />
            </TabPane>
          </Tabs>
          {showImportModal && (
            <ImportFileModal
              csvErrorMessage=""
              closePopup={() => setShowImportModal(false)}
              clearErrorMessage={() => {}}
              handleUploadFile={handelAddXLS}
              templatePath="admin/template/xls/online_order_products_template.xlsx"
              fileType=".xlsx"
              noticeText="註：匯入上限為 1000 筆"
            />
          )}
        </>
      ) : (
        <Skeleton />
      )}
      <div style={{ position: "absolute", top: 9, right: 20 }}>
        {activeTab === "order" ? (
          <Space>
            <Button type="primary" onClick={() => setShowPopup(true)} disabled={cannotEdit}>
              編輯
            </Button>
          </Space>
        ) : (
          <Button type="default" onClick={handleSubmit} disabled={cannotEdit || orderProductList.results.length === 0}>
            提交
          </Button>
        )}
      </div>
      {showProductFormModal && (
        <EditProductPopup
          onClose={() => setShowProductFormModal(false)}
          currentProduct={currentProduct}
          warehouseCode={onlineOrderDetail.warehouseCode}
          isB2B={isB2B}
          warehouseIsTcat={warehouseIsTcat}
        />
      )}
      {showPopup && <EditPopup onlineOrderDetail={onlineOrderDetail} onClose={() => setShowPopup(false)} />}
    </StyledWrapper>
  );
}
